import type { Application } from '@core/types';
import type { ApplicationStatus } from '@core/enums';
import type { AiInterviewSessionType } from '@core/supabase/services/aiInterviewSessionService';

/**
 * Business rules (PRD §4.3 pipeline / client engagement):
 * AI interviews are tied to an application. The pipeline must move past raw submission
 * so the client/recruiter has engaged the candidate before scheduling a browser AI round.
 *
 * - Screening: allowed from "Viewed by client" onward (and shortlisted / interview stages).
 * - Technical / behavioral: require "Shortlisted" or a later stage (stronger client engagement).
 *
 * Terminal / closed applications never qualify.
 */
const TERMINAL: ApplicationStatus[] = ['rejected', 'placed', 'closed', 'offer_rejected', 'no_show'];

/** Stages that qualify for screening AI interview (not stuck on submitted only). */
const ELIGIBLE_SCREENING: ApplicationStatus[] = [
  'viewed_by_client',
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'on_hold',
];

/** Stages for technical / behavioral AI rounds (client has short-listed or progressed). */
const ELIGIBLE_STRICT: ApplicationStatus[] = [
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'on_hold',
];

export function isTerminalApplicationStatus(status: ApplicationStatus): boolean {
  return TERMINAL.includes(status);
}

export function isEligibleApplicationForAiInterview(
  status: ApplicationStatus,
  sessionType: AiInterviewSessionType
): boolean {
  if (TERMINAL.includes(status)) return false;
  return sessionType === 'screening'
    ? ELIGIBLE_SCREENING.includes(status)
    : ELIGIBLE_STRICT.includes(status);
}

export function getEligibleApplicationsForAiInterview(
  applications: Application[],
  sessionType: AiInterviewSessionType
): Application[] {
  return applications.filter((a) => isEligibleApplicationForAiInterview(a.status, sessionType));
}

export type AiInterviewEligibility = {
  ok: boolean;
  /** Shown when ok is false */
  message: string;
  eligibleApps: Application[];
};

export function evaluateAiInterviewEligibility(
  applications: Application[],
  sessionType: AiInterviewSessionType
): AiInterviewEligibility {
  const eligible = getEligibleApplicationsForAiInterview(applications, sessionType);
  if (eligible.length > 0) {
    return { ok: true, message: '', eligibleApps: eligible };
  }
  if (applications.length === 0) {
    return {
      ok: false,
      message:
        'Create an application for this candidate first. AI interview links are tied to an application once the pipeline moves past initial submission (client / recruiter review).',
      eligibleApps: [],
    };
  }
  const onlySubmitted = applications.every((a) => a.status === 'submitted');
  if (onlySubmitted) {
    return {
      ok: false,
      message:
        'Applications are still at "Submitted". Move at least one application to "Viewed by client" or beyond before scheduling an AI interview — the hiring client should review the candidate first.',
      eligibleApps: [],
    };
  }
  if (sessionType !== 'screening') {
    return {
      ok: false,
      message:
        'Technical and behavioral AI rounds require an application at "Shortlisted" or a later stage. Screening is available from "Viewed by client" onward.',
      eligibleApps: [],
    };
  }
  return {
    ok: false,
    message:
      'No qualifying application: stages must be at least "Viewed by client" or "Shortlisted", and not terminal (rejected, placed, closed, etc.).',
    eligibleApps: [],
  };
}
